import type { CSSProperties } from "react";

export type BubbleColor =
  | "orange"
  | "blue"
  | "pink"
  | "purple"
  | "green"
  | "amber"
  | "rose"
  | "indigo"
  | "emerald"
  | "red"
  | "cyan"
  | "fuchsia";

export type FloatAnimation = "float-1" | "float-2" | "float-3";

export type BubbleConfig = {
  color: BubbleColor;
  top?: string;
  left?: string;
  right?: string;
  bottom?: string;
  size: number;
  opacity?: number;
  animation?: FloatAnimation;
  direction?: "alternate" | "alternate-reverse";
  durationSec?: number;
};

const PALETTE: Record<BubbleColor, string> = {
  orange: "251, 146, 60",
  blue: "96, 165, 250",
  pink: "244, 114, 182",
  purple: "243, 6, 131",
  green: "59, 130, 246",
  amber: "251, 191, 36",
  rose: "243, 6, 131",
  indigo: "37, 99, 235",
  emerald: "37, 99, 235",
  red: "239, 68, 68",
  cyan: "14, 165, 233",
  fuchsia: "217, 70, 239",
};

const FLOAT_ANIMATIONS: FloatAnimation[] = ["float-1", "float-2", "float-3"];

export function bubbleInlineStyle(
  config: BubbleConfig,
  reducedMotion = false,
  isStatic = false
): CSSProperties {
  const opacity = config.opacity ?? 0.65;
  const rgb = PALETTE[config.color];
  const animation = config.animation ?? "float-1";
  const direction = config.direction ?? "alternate";
  const durationSec = config.durationSec ?? 14;

  const animationString = isStatic
    ? "none"
    : `${animation} ${durationSec}s infinite ${direction}`;

  return {
    top: config.top,
    left: config.left,
    right: config.right,
    bottom: config.bottom,
    width: config.size,
    height: config.size,
    background: `radial-gradient(circle at center, rgba(${rgb}, ${opacity}) 0%, rgba(${rgb}, 0) 85%)`,
    boxShadow: `0 0 60px 30px rgba(${rgb}, ${opacity * 0.38}), inset 0 0 50px rgba(${rgb}, ${opacity * 0.48})`,
    animation: reducedMotion ? "none" : animationString,
  };
}

function bandBubble(
  color: BubbleColor,
  top: string,
  horizontal: { left?: string; right?: string },
  size: number,
  index: number
): BubbleConfig {
  return {
    color,
    top,
    ...horizontal,
    size,
    opacity: 0.62 + (index % 3) * 0.04,
    animation: FLOAT_ANIMATIONS[index % 3],
    direction: index % 2 === 0 ? "alternate" : "alternate-reverse",
    durationSec: 12 + (index % 5),
  };
}

function softBubble(
  color: BubbleColor,
  top: string,
  horizontal: { left?: string; right?: string },
  size: number,
  index: number,
  opacity?: number
): BubbleConfig {
  return {
    color,
    top,
    ...horizontal,
    size,
    opacity: opacity ?? 0.28 + (index % 2) * 0.04,
    animation: FLOAT_ANIMATIONS[index % 3],
    direction: index % 2 === 0 ? "alternate" : "alternate-reverse",
    durationSec: 12 + (index % 5),
  };
}

function softBottomBubble(
  color: BubbleColor,
  bottom: string,
  horizontal: { left?: string; right?: string },
  size: number,
  index: number,
  opacity?: number
): BubbleConfig {
  return {
    color,
    bottom,
    ...horizontal,
    size,
    opacity: opacity ?? 0.28 + (index % 2) * 0.04,
    animation: FLOAT_ANIMATIONS[index % 3],
    direction: index % 2 === 0 ? "alternate" : "alternate-reverse",
    durationSec: 12 + (index % 5),
  };
}

/** Full-page scroll bubbles for the home layout. */
export function buildHomePageBubbles(): BubbleConfig[] {
  const bubbles: BubbleConfig[] = [];
  const mainColors: BubbleColor[] = ["orange", "blue", "pink"];
  const bandPositions = [
    { right: "10%" },
    { left: "15%" },
    { right: "25%" },
    { right: "5%" },
    { left: "20%" },
    { right: "15%" },
  ] as const;

  for (let band = 0; band < 13; band++) {
    const top = `${5 + band * 20}%`;
    for (let j = 0; j < 3; j++) {
      bubbles.push(
        softBubble(
          mainColors[j],
          top,
          bandPositions[(band * 3 + j) % bandPositions.length],
          580 + ((band + j) % 4) * 35,
          band * 3 + j
        )
      );
    }
  }

  const rightAccents: Array<[number, BubbleColor]> = [
    [35, "purple"],
    [75, "green"],
    [115, "amber"],
    [155, "rose"],
    [195, "indigo"],
    [235, "emerald"],
    [275, "orange"],
  ];

  rightAccents.forEach(([top, color], i) => {
    bubbles.push(
      softBubble(color, `${top}%`, { right: `${1 + (i % 3)}%` }, 500 + (i % 3) * 20, i + 40)
    );
  });

  const leftAccents: Array<[number, BubbleColor]> = [
    [5, "red"],
    [45, "blue"],
    [85, "fuchsia"],
    [125, "cyan"],
  ];

  leftAccents.forEach(([top, color], i) => {
    bubbles.push(
      softBubble(color, `${top}%`, { left: `${1 + (i % 3)}%` }, 500 + (i % 3) * 25, i + 50)
    );
  });

  return bubbles;
}

export const HOME_PAGE_BUBBLES = buildHomePageBubbles();

/** Hero text-readability veil — fades out so bubbles stay visible. */
export const HERO_VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255,255,255,0.93) 0%, rgba(255,255,255,0.85) 35%, rgba(255,255,255,0.4) 65%, transparent 100%)";

/** Unified veil for Hero + Welcome — readable at top, fades early. */
export const HERO_ABOUT_VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.55) 18%, rgba(255,255,255,0.28) 36%, rgba(255,255,255,0.08) 55%, transparent 72%)";

/** Light veil for connected white sections — bubbles stay visible. */
export const CONNECTED_VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0.08) 45%, transparent 80%)";

/** Bubbles spanning Hero and Welcome as one visual block. */
export const HERO_ABOUT_BUBBLES: BubbleConfig[] = [
  {
    color: "orange",
    top: "-6%",
    left: "8%",
    size: 680,
    opacity: 0.72,
    animation: "float-1",
    durationSec: 12,
  },
  {
    color: "blue",
    top: "3%",
    right: "14%",
    size: 720,
    opacity: 0.72,
    animation: "float-2",
    durationSec: 14,
  },
  {
    color: "pink",
    top: "26%",
    left: "10%",
    size: 620,
    opacity: 0.68,
    animation: "float-3",
    durationSec: 13,
  },
  softBubble("blue", "42%", { right: "-6%" }, 440, 0),
  softBubble("orange", "58%", { left: "-8%" }, 400, 1),
  softBubble("pink", "74%", { right: "2%" }, 360, 2),
];

/** Unified veil for Services + Features — soft through both headings. */
export const SERVICES_FEATURES_VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.65) 22%, rgba(255,255,255,0.42) 42%, rgba(255,255,255,0.18) 62%, transparent 80%)";

/** Bubbles spanning Services (roadmap) and Features (why choose us). */
export const SERVICES_FEATURES_BUBBLES: BubbleConfig[] = [
  bandBubble("purple", "5%", { right: "-5%" }, 430, 0),
  bandBubble("amber", "28%", { left: "-8%" }, 390, 1),
  bandBubble("pink", "48%", { right: "4%" }, 370, 2),
  bandBubble("orange", "55%", { left: "-6%" }, 400, 3),
  bandBubble("cyan", "76%", { right: "-4%" }, 380, 4),
];

/** Veil for Services → Features → States grid (one white block). */
export const CARE_PATH_VEIL_GRADIENT = CONNECTED_VEIL_GRADIENT;

export const CARE_PATH_BUBBLES: BubbleConfig[] = [
  softBubble("purple", "4%", { right: "-5%" }, 430, 0),
  softBubble("amber", "18%", { left: "-8%" }, 390, 1),
  softBubble("pink", "32%", { right: "4%" }, 370, 2),
  softBubble("orange", "42%", { left: "-6%" }, 400, 3),
  softBubble("cyan", "54%", { right: "-4%" }, 380, 4),
  softBubble("blue", "66%", { right: "-8%" }, 460, 5),
  softBubble("fuchsia", "78%", { left: "-10%" }, 420, 6),
  softBubble("emerald", "90%", { right: "-5%" }, 400, 7),
];

/** After Universities — very light veil; blog/social area stays mostly clear. */
export const AFTER_UNI_VEIL_GRADIENT =
  "linear-gradient(to bottom, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.04) 30%, transparent 62%)";

/** Partners (Friends with Brain Benefits) — glow at bottom of section. */
export const PARTNERS_BUBBLES: BubbleConfig[] = [
  softBottomBubble("amber", "-10%", { right: "-6%" }, 340, 0, 0.14),
  softBottomBubble("pink", "0%", { left: "-10%" }, 360, 1, 0.16),
  softBottomBubble("purple", "10%", { right: "-8%" }, 340, 2, 0.14),
];

/** After Universities — team through contact (partners bubbles are local). */
export const AFTER_UNI_BUBBLES: BubbleConfig[] = [
  softBubble("blue", "84%", { right: "-6%" }, 320, 3),
  softBubble("green", "93%", { left: "-8%" }, 300, 4),
];

export const COMMUNITY_VEIL_GRADIENT = CONNECTED_VEIL_GRADIENT;

export const COMMUNITY_BUBBLES = AFTER_UNI_BUBBLES;

export const LOWER_VEIL_GRADIENT = CONNECTED_VEIL_GRADIENT;

export const LOWER_BUBBLES: BubbleConfig[] = [];

export const HOME_DECOR_ZONES = {
  heroAbout: {
    bubbles: HERO_ABOUT_BUBBLES,
    veil: HERO_ABOUT_VEIL_GRADIENT,
  },
  mid: {
    bubbles: CARE_PATH_BUBBLES,
    veil: CONNECTED_VEIL_GRADIENT,
  },
  afterUni: {
    bubbles: AFTER_UNI_BUBBLES,
    veil: AFTER_UNI_VEIL_GRADIENT,
  },
} as const;

export type HomeDecorZone = keyof typeof HOME_DECOR_ZONES;

/** Section-local presets — positions are % within the section container. */
export const DECOR_PRESETS = {
  hero: [
    {
      color: "orange",
      top: "-15%",
      left: "10%",
      size: 650,
      opacity: 0.7,
      animation: "float-1",
      durationSec: 12,
    },
    {
      color: "blue",
      top: "10%",
      right: "20%",
      size: 700,
      opacity: 0.7,
      animation: "float-2",
      durationSec: 14,
    },
    {
      color: "pink",
      bottom: "-10%",
      left: "15%",
      size: 600,
      opacity: 0.7,
      animation: "float-3",
      durationSec: 13,
    },
  ],
  about: [
    bandBubble("blue", "8%", { right: "-8%" }, 420, 0),
    bandBubble("orange", "55%", { left: "-10%" }, 380, 1),
    bandBubble("pink", "78%", { right: "5%" }, 340, 2),
  ],
  insurance: [
    bandBubble("blue", "12%", { left: "-6%" }, 360, 0),
    bandBubble("green", "70%", { right: "-4%" }, 320, 1),
  ],
  services: [
    bandBubble("purple", "10%", { right: "-5%" }, 400, 0),
    bandBubble("amber", "60%", { left: "-8%" }, 360, 1),
    bandBubble("pink", "85%", { right: "8%" }, 300, 2),
  ],
  features: [
    bandBubble("orange", "15%", { left: "-6%" }, 380, 0),
    bandBubble("cyan", "50%", { right: "-4%" }, 350, 1),
  ],
  states: [
    bandBubble("blue", "5%", { right: "-10%" }, 480, 0),
    bandBubble("fuchsia", "40%", { left: "-12%" }, 440, 1),
    bandBubble("emerald", "75%", { right: "-6%" }, 400, 2),
  ],
  universities: [
    bandBubble("indigo", "20%", { left: "-8%" }, 340, 0),
    bandBubble("rose", "65%", { right: "-5%" }, 320, 1),
  ],
  partners: [
    bandBubble("amber", "10%", { right: "-6%" }, 360, 0),
    bandBubble("blue", "80%", { left: "-8%" }, 340, 1),
  ],
  team: [
    bandBubble("pink", "8%", { left: "-10%" }, 400, 0),
    bandBubble("purple", "72%", { right: "-8%" }, 380, 1),
  ],
  testimonials: [
    bandBubble("orange", "5%", { right: "-8%" }, 460, 0),
    bandBubble("pink", "35%", { left: "-10%" }, 420, 1),
    bandBubble("blue", "68%", { right: "0%" }, 380, 2),
    bandBubble("rose", "88%", { left: "-5%" }, 360, 3),
  ],
  blog: [
    bandBubble("cyan", "15%", { right: "-6%" }, 320, 0),
    bandBubble("indigo", "70%", { left: "-8%" }, 300, 1),
  ],
  faq: [
    bandBubble("green", "12%", { left: "-8%" }, 340, 0),
    bandBubble("blue", "75%", { right: "-6%" }, 320, 1),
  ],
} as const satisfies Record<string, BubbleConfig[]>;

export type DecorPreset = keyof typeof DECOR_PRESETS;
